import { useCallback, useEffect, useRef, useState } from 'react';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';
import { useResolvedGameAudioOn } from '@/shared/hooks/useGameAudioSettings';

interface AudioRequest {
	id: number;
	onEnd?: () => void;
	url: string;
}

export function useExternalAudio() {
	const audioOn = useResolvedGameAudioOn();
	const [request, setRequest] = useState<AudioRequest | null>(null);
	const onEndRef = useRef<(() => void) | undefined>(undefined);
	const playedRequestIdRef = useRef<number | null>(null);
	const handleEnd = useCallback(() => {
		const onEnd = onEndRef.current;
		onEndRef.current = undefined;
		onEnd?.();
	}, []);
	const { play, stop } = useAudioPlayer({
		src: request?.url,
		html5: true,
		onEnd: handleEnd,
	});

	useEffect(() => {
		if (!request || playedRequestIdRef.current === request.id) {
			return;
		}

		playedRequestIdRef.current = request.id;
		if (!audioOn) {
			request.onEnd?.();
			return;
		}

		stop();
		onEndRef.current = request.onEnd;
		play();
	}, [audioOn, play, request, stop]);

	useEffect(() => {
		if (!audioOn && onEndRef.current) {
			handleEnd();
		}
	}, [audioOn, handleEnd]);

	return useCallback(
		(url?: string, onEnd?: () => void) => {
			const normalizedUrl = url?.trim();
			if (!audioOn || !normalizedUrl) {
				onEnd?.();
				return;
			}

			setRequest((currentRequest) => ({
				id: (currentRequest?.id ?? 0) + 1,
				onEnd,
				url: normalizedUrl,
			}));
		},
		[audioOn],
	);
}
